import { apiClient, apiClientPublic } from "services/api-client";

/**
 * Assessment API Service
 * Manages vendor security assessments, submission workflows, and trust scoring
 */

// ========== ASSESSMENT CRUD OPERATIONS ==========

/**
 * Get all assessments with optional filters
 */
export const getAssessments = async (tenant, filters = {}) => {
  let query = `/vendor/assessment?companyKey=${tenant}`;

  if (filters.status) {
    query += `&status=${filters.status}`;
  }
  if (filters.vendorId) {
    query += `&vendorId=${filters.vendorId}`;
  }
  if (filters.frameworkId) {
    query += `&frameworkId=${filters.frameworkId}`;
  }
  if (filters.page && filters.perPage) {
    query += `&page=${filters.page}&limit=${filters.perPage}`;
  }

  return await apiClient.get(query);
};

/**
 * Get a single assessment by ID
 */
export const getAssessmentById = async (id) => {
  return await apiClient.get(`/vendor/assessment/${id}`);
};

/**
 * Generate a new assessment for a vendor
 */
export const generateAssessment = async (assessmentData) => {
  return await apiClient.post('/vendor/assessment', assessmentData);
};

/**
 * Send initial invitation email to vendor
 */
export const sendInvitation = async (id) => {
  return await apiClient.post(`/vendor/assessment/${id}/send-invitation`);
};

/**
 * Resend invitation email to vendor
 */
export const resendInvitation = async (id) => {
  return await apiClient.post(`/vendor/assessment/${id}/resend-invitation`);
};

// ========== VENDOR SUBMISSION OPERATIONS (Token-based) ==========

/**
 * Get assessment by vendor access token (public, no auth)
 * Returns both assessment metadata and control responses in a single call
 */
export const getAssessmentByToken = async (token) => {
  return await apiClientPublic.get(`/vendor-submission/${token}/assessment`);
};

/**
 * Update vendor response to control question
 */
export const updateVendorResponse = async (token, controlResponseId, data) => {
  const body = {};
  if (data.maturityLevel !== undefined) {
    body.vendorMaturityLevel = data.maturityLevel;
  }
  if (data.notes !== undefined) {
    body.notes = data.notes;
  }
  return await apiClientPublic.put(`/vendor-submission/${token}/control-response/${controlResponseId}`, body);
};

/**
 * Upload evidence file for a control
 */
export const uploadEvidence = async (token, controlResponseId, formData) => {
  return await apiClientPublic.post(`/vendor-submission/${token}/control-response/${controlResponseId}/evidence`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

/**
 * Submit completed vendor assessment
 */
export const submitVendorAssessment = async (token) => {
  return await apiClientPublic.post(`/vendor-submission/${token}/submit`);
};

// ========== INTERNAL REVIEW OPERATIONS ==========

/**
 * Get control responses with details for gap analysis
 */
export const getControlResponses = async (assessmentId) => {
  return await apiClient.get(`/vendor/assessment/${assessmentId}/control-responses`);
};

/**
 * Get trust score for an assessment
 */
export const getTrustScore = async (assessmentId) => {
  return await apiClient.get(`/vendor/assessment/${assessmentId}/trust-score`);
};

/**
 * Unlock controls for vendor remediation
 */
export const unlockControls = async (assessmentId, controlIds, notes) => {
  return await apiClient.put(`/vendor/assessment/${assessmentId}/unlock`, {
    controlIds,
    notes
  });
};

/**
 * Approve an assessment
 */
export const approveAssessment = async (assessmentId, notes) => {
  return await apiClient.put(`/vendor/assessment/${assessmentId}/approve`, {
    notes
  });
};

/**
 * Request executive approval for high-risk assessment
 */
export const requestExecutiveApproval = async (assessmentId, executiveUserId, justification) => {
  return await apiClient.post(`/vendor/assessment/${assessmentId}/request-approval`, {
    executiveUserId,
    justification
  });
};

// ========== EXECUTIVE APPROVAL OPERATIONS ==========

/**
 * Approve an assessment as executive
 */
export const executiveApproveAssessment = async (assessmentId, justification) => {
  return await apiClient.post(`/vendor/executive-approvals/${assessmentId}/approve`, { justification });
};

/**
 * Reject an assessment as executive
 */
export const executiveRejectAssessment = async (assessmentId, justification) => {
  return await apiClient.post(`/vendor/executive-approvals/${assessmentId}/reject`, { justification });
};

/**
 * Get executive approval history for an assessment
 */
export const getExecutiveApprovalHistory = async (assessmentId) => {
  return await apiClient.get(`/vendor/executive-approvals/assessment/${assessmentId}`);
};

/**
 * Get evidence file download URL
 */
export const getEvidenceDownloadUrl = async (fileId) => {
  return await apiClient.get(`/vendor/evidence/${fileId}/download`);
};

/**
 * Get activity timeline for an assessment
 */
export const getAssessmentTimeline = async (assessmentId) => {
  return await apiClient.get(`/vendor/assessment/${assessmentId}/timeline`);
};
